import React, { useEffect, useState, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { fetchNotifications } from '../../store/slices/notificationsSlice';
import { chatAPI, workplansAPI, tasksAPI } from '../../services/api';
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  BuildingOfficeIcon,
  DocumentChartBarIcon,
  ChartBarIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
  Cog6ToothIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const MainLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { unreadCount } = useSelector((state) => state.notifications);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [tasksBadge, setTasksBadge] = useState(0);

  // Fetch chat unread count
  const fetchChatUnreadCount = useCallback(async () => {
    try {
      const response = await chatAPI.getRooms();
      const rooms = response.data.results || response.data || [];
      const totalUnread = rooms.reduce((sum, room) => sum + (room.unread_count || 0), 0);
      setChatUnreadCount(totalUnread);
    } catch (error) {
      console.error('Chat unread count error:', error);
    }
  }, []);

  // Fetch overdue items count
  const fetchOverdueCount = useCallback(async () => {
    try {
      const response = await workplansAPI.getOverdueItems();
      console.log('Overdue response:', response.data);
      const count = response.data.count || 0;
      console.log('Overdue count:', count);
      setOverdueCount(count);
    } catch (error) {
      console.error('Overdue count error:', error);
    }
  }, []);

  // Fetch tasks badge count
  const fetchTasksBadge = useCallback(async () => {
    try {
      const response = await tasksAPI.getBadgeCounts();
      const data = response.data;
      // Yangi + muddati o'tgan
      setTasksBadge(data.total_badge || (data.new_count || 0) + (data.overdue_count || 0));
    } catch (error) {
      console.error('Tasks badge error:', error);
    }
  }, []);

  useEffect(() => {
    dispatch(fetchNotifications());
    fetchChatUnreadCount();
    fetchOverdueCount();
    fetchTasksBadge();
    
    // Refresh notifications every 30 seconds
    const interval = setInterval(() => {
      dispatch(fetchNotifications());
      fetchChatUnreadCount();
      fetchOverdueCount();
      fetchTasksBadge();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [dispatch, fetchChatUnreadCount, fetchOverdueCount, fetchTasksBadge]);

  useEffect(() => {
    // Close sidebar on route change (mobile)
    setSidebarOpen(false);
  }, [location]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', icon: HomeIcon, label: 'Bosh sahifa' },
    { path: '/tasks', icon: ClipboardDocumentListIcon, label: 'Topshiriqlar', badge: tasksBadge, badgeColor: 'bg-red-500' },
    { path: '/workplans', icon: CalendarIcon, label: 'Ish reja', badge: overdueCount, badgeColor: 'bg-orange-500' },
    { path: '/reports', icon: DocumentChartBarIcon, label: 'Hisobotlar' },
    { path: '/kpi', icon: ChartBarIcon, label: 'KPI' },
    { path: '/monitoring', icon: ComputerDesktopIcon, label: 'Monitoring' },
    { path: '/risks', icon: ExclamationTriangleIcon, label: 'Risklar' },
    { path: '/documents', icon: DocumentTextIcon, label: 'Hujjatlar' },
    { path: '/chat', icon: ChatBubbleLeftRightIcon, label: 'Chat', badge: chatUnreadCount },
    { path: '/archive', icon: ArchiveBoxIcon, label: 'Arxiv' },
  ];

  const adminMenuItems = [
    { path: '/organizations', icon: BuildingOfficeIcon, label: 'Tashkilotlar' },
    { path: '/users', icon: UsersIcon, label: 'Foydalanuvchilar' },
  ];

  const getPageTitle = () => {
    const allItems = [...menuItems, ...adminMenuItems, { path: '/settings', label: 'Sozlamalar' }, { path: '/notifications', label: 'Xabarnomalar' }];
    const current = allItems.find(item => location.pathname.startsWith(item.path));
    return current?.label || 'AuditKPI';
  };

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b">
        <h1 className="text-xl font-bold text-blue-600">AuditKPI.uz</h1>
        <p className="text-xs text-gray-500 mt-1">Ichki Audit Tizimi</p>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''}`
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="flex-1">{item.label}</span>
            {item.badge > 0 && (
              <span className={`min-w-[20px] h-5 px-1.5 ${item.badgeColor || 'bg-red-500'} text-white text-xs rounded-full flex items-center justify-center`}>
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </NavLink>
        ))}

        {user?.role === 'admin' && (
          <>
            <div className="pt-4 mt-4 border-t">
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase mb-2">Admin</p>
            </div>
            {adminMenuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="p-4 border-t">
        <NavLink to="/settings" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <Cog6ToothIcon className="w-5 h-5" />
          <span>Sozlamalar</span>
        </NavLink>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg flex flex-col transform transition-transform duration-300 lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Mobile close button */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="absolute top-4 right-4 p-2 lg:hidden"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
        
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm h-16 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 lg:hidden"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{getPageTitle()}</h2>
              <p className="text-sm text-gray-500 hidden sm:block">
                {user?.organization_name || user?.role_display || user?.role}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <NavLink
              to="/notifications"
              className="relative p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <BellIcon className="w-6 h-6" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </NavLink>

            <div className="flex items-center gap-3 pl-2 sm:pl-4 border-l">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-800">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-500">{user?.username}</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold">
                  {user?.first_name?.[0] || user?.username?.[0]?.toUpperCase()}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Chiqish"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
